import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { LinearProgress, Box, Button, Tab, Tabs } from '@mui/material'
import { studentApiSlice } from '../../../features/student/studentSlice'
import { H3 } from '../../../layouts/TextStyles'
import { userApiSlice } from '../../../features/user/userSlice'
import CustomInput from '../../../components/muiComponents/customInput'
import StudentsTable from './components/StudentsTable'
import AddStudentDialog from './components/AddStudentDialog'
import SingleSelect from '../../../components/muiComponents/singleSelect/SingleSelect'
import { courseApiSlice } from '../../../features/course/courseSlice'
import { subjectApiSlice } from '../../../features/subject/subjectSlice'
import { NewStudentState } from '../../../features/types'



const NewStudentListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage } = useIntl()
    const { data: courses = [], isFetching: isFetchingCourses } =
        courseApiSlice.useFetchAllCoursesQuery()
    const {
        data: subjects = [],
    } = subjectApiSlice.useFetchAllSubjectsQuery()
    const [search, setSearch] = useState('')
    const [selectedCourse, setSelectedCourse] = useState<string | null>(null)
    const [selectedSubject, setSelectedSubject] = useState<string | null>(null)
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null)
    const [type, setType] = useState(searchParams.get('type') || "PAID_ONLY")
    const [page, setPage] = React.useState(
        Number(searchParams.get('page')) || 1,
    )
    const [rowsPerPage, setRowsPerPage] = React.useState(
        Number(searchParams.get('size')) || 5,
    )
    const [fetchStudents, { data: studentResponse = null, isFetching: isFetchingStudents }] = studentApiSlice.useLazyFetchFilteredStudentsQuery();

    const [deleteUser, { isLoading: isDeleting }] =
        userApiSlice.useDeleteUserMutation()
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()
    const [sendWelcomeMessage, { isLoading: isSendingWelcomeMessage }] =
        userApiSlice.useSendWelcomeMessageMutation()
    const buildFilterPayload = () => ({
        courseId: selectedCourse ? Number(selectedCourse) : null,
        subjectId: selectedSubject ? Number(selectedSubject) : null,
        language: selectedLanguage ?? null,
    })

    const searchStudents = (resetPage = false) => {
        if (resetPage && page !== 1) setPage(1) // поиск баскан кезде сбрасываем page, чтобы через useEffect шакырылу ушин
        else fetchStudents({ page: page - 1, size: rowsPerPage, filter: buildFilterPayload(), type: type, search: search });
    };
    const handleDelete = async (id: number) => {
        try {
            await deleteUser(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            searchStudents()
        } catch (error) { }
    }

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            searchStudents()
        } catch (error) { }
    }

    const handleSendWelcomeMessage = async (id: number) => {
        try {
            await sendWelcomeMessage(id).unwrap()
            toast.success(formatMessage({ id: 'sentSuccess' }))
            searchStudents()
        } catch (error) { }
    }

    useEffect(() => {
        const params = {
            page: page.toString(),
            size: rowsPerPage.toString(),
            filter: search,
            type: type,
        }
        setSearchParams(params)
        searchStudents()
    }, [rowsPerPage, page, type, search])

    useEffect(() => {
        searchStudents()
    }, []);

    const handleCourseFilter = (value: string | null) => {
        setSelectedCourse(value)
        fetchStudents({ page: 0, size: rowsPerPage, filter: { ...buildFilterPayload(), courseId: value ? Number(value) : null }, search, type })
    }

    const handleSubjectFilter = (value: string | null) => {
        setSelectedSubject(value)
        fetchStudents({ page: 0, size: rowsPerPage, filter: { ...buildFilterPayload(), subjectId: value ? Number(value) : null }, search, type })
    }

    const handleLanguageFilter = (value: string | null) => {
        setSelectedLanguage(value)
        fetchStudents({ page: 0, size: rowsPerPage, filter: { ...buildFilterPayload(), language: value ?? null }, search, type })
    }
    const handleChangeType = (e, newType) => {
        setType(newType)
        searchStudents()
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'students' })}</H3>
                    </Title>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <SingleSelect
                            value={selectedCourse ? Number(selectedCourse) : ''}
                            placeholder={formatMessage({ id: 'course' })}
                            handleChange={(e) => handleCourseFilter(e.target.value ? String(e.target.value) : null)}
                            menuItems={[{ id: 0, title: formatMessage({ id: 'all' }) }, ...courses]}
                            valueField={'id'}
                            loading={isFetchingCourses}
                            small
                        />
                        <SingleSelect
                            value={selectedSubject ? Number(selectedSubject) : ''}
                            placeholder={formatMessage({ id: 'subject' })}
                            handleChange={(e) => handleSubjectFilter(e.target.value ? String(e.target.value) : null)}
                            menuItems={[{ id: 0, title: formatMessage({ id: 'all' }), }, ...subjects]}
                            valueField={'id'}
                            small
                        />
                        <SingleSelect
                            value={selectedLanguage || ''}
                            placeholder={formatMessage({ id: 'language' })}
                            handleChange={(e) => handleLanguageFilter(e.target.value ? String(e.target.value) : null)}
                            menuItems={[{ id: 0, title: formatMessage({ id: 'all' }) }, { id: 'KZ', title: 'KZ' }, { id: 'RU', title: 'RU' }]}
                            valueField={'id'}
                            small
                        />
                    </Box>



                    <Box sx={styles.filterBox}>
                        <Tabs value={type} onChange={handleChangeType} aria-label="lab API tabs example">
                            <Tab label={formatMessage({ id: 'paid_only' })} value="PAID_ONLY" />
                            <Tab label={formatMessage({ id: 'studied' })} value="STUDIED" />
                            <Tab label={formatMessage({ id: 'all' })} value="ALL" />
                        </Tabs>
                    </Box>

                    <AddStudentDialog refetchStudents={() => searchStudents()} />
                </Header>

                <SearchBox>
                    <CustomInput
                        sx={{ width: "400px" }}
                        margin="none"
                        type="text"
                        placeholder={formatMessage({ id: 'search' })}
                        size="small"
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <Button
                        variant="contained"
                        onClick={() => searchStudents(true)}
                    >
                        {formatMessage({ id: 'search' })}
                    </Button>
                </SearchBox>

                {isFetchingStudents || isReseting || isDeleting ? (
                    <LinearProgress />
                ) : (
                    studentResponse && (
                        <StudentsTable
                            studentsData={studentResponse.data as NewStudentState[]}
                            pageNumber={studentResponse.pageNumber}
                            totalCount={studentResponse.totalCount}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            isFetching={isFetchingStudents}
                            isLoadingUsersInfo={false}
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                            onDelete={handleDelete}
                            onResetPassword={handleResetPassword}
                            onSendWelcomeMessage={handleSendWelcomeMessage}
                        />
                    )
                )}
            </Content>
        </Wrapper>
    )
}

export default NewStudentListPage


const styles = {
    filterBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    totalSum: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    totalInfoBox: {
        fontSize: '18px',
        color: '#404a51',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}



const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const SearchBox = styled(Box)`
    display: flex; 
    align-items: center;
    gap: 8px;
    margin: 8px 0;
`
